import Link from 'next/link'
import React, { useState } from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle'
import { faSpinnerThird } from '@fortawesome/pro-light-svg-icons/faSpinnerThird'
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation'
import axios from 'axios'
import clsx from 'clsx'

import { ActionLink } from '@infura/packages.ui/ActionLink'
import { Button } from '@infura/packages.ui/Button'
import { Codeblock } from '@infura/packages.ui/Codeblock'
import { Heading } from '@infura/packages.ui/Heading'
import { Select } from '@infura/packages.ui/Select'
import { Text } from '@infura/packages.ui/Text'

import { IStepsComponent } from 'components/gettingStartedTour/types'

const exampleRequests = [
  {
    params: ['latest'],
    text: 'Latest Transaction',
    value: 'eth_getBlockTransactionCountByNumber'
  },
  {
    params: [],
    text: 'Latest Block Number',
    value: 'eth_blockNumber'
  }
]

const StepSendFirstRequest = ({ apiKeySelected, handleNextStep, handlePrevStep, subnetUrl }: IStepsComponent) => {
  const [currentExample, setCurrentExample] = useState(exampleRequests[1])
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [isError, setIsError] = useState(false)

  const handleRequest = () => {
    setIsLoading(true)
    setSuccess('')
    setIsError(false)

    axios
      .post(
        `https://${subnetUrl}/${apiKeySelected}`,
        { id: 1, jsonrpc: '2.0', method: currentExample.value, params: currentExample.params },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      )
      .then(({ data }) => {
        setSuccess(data)
        setIsError(false)
      })
      .catch(() => {
        setSuccess('')
        setIsError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <>
      <div className='pb-4'>
        <div className='lg:flex lg:items-center lg:justify-between'>
          <div>
            <Heading as='h4' className='mb-2'>
              Send your first request
            </Heading>
            <Text className='mb-4 lg:pr-24'>
              <span>To send your first request, click “Send request” below. </span>
              <span className='hidden lg:inline'>
                You have configured your API key to require API key secret, JWTs, or allowlists please disable these in
                your settings before sending your first request.
              </span>
            </Text>
          </div>
          <div>
            <Select
              buttonStyles={clsx('pl-4 lg:w-[220px]')}
              className='xs:w-full mb-4 lg:!w-[220px]'
              onChange={option => {
                setCurrentExample(exampleRequests.filter(request => request.value === option.value)[0])
              }}
              options={exampleRequests.map(example => ({ text: example.text, value: example.value }))}
              placeholder='Latest Block Number'
              // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
              value={null}
            />
          </div>
        </div>
        <Codeblock
          className='!rounded-b-none'
          hasCopyButton
          preClassName='break-all whitespace-pre-wrap'
          theme='dark'
          type='multi-line'
        >
          {`curl --url https://${subnetUrl}/${apiKeySelected} \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","method":"${currentExample.value}","params":${JSON.stringify(currentExample.params)},"id":1}'`}
        </Codeblock>
        <div className='bg-background-alternative border-t-none border-border-muted flex justify-end rounded-bl-md rounded-br-md border p-4'>
          <ActionLink
            className=''
            data-testid='send request'
            disabled={isLoading}
            onClick={e => {
              e.preventDefault()
              handleRequest()
            }}
          >
            {isLoading ? <FontAwesomeIcon className='fa-fw fa-spin mr-1' icon={faSpinnerThird} /> : null}
            <span>Send Request </span>
            <FontAwesomeIcon className='-mt-[2px] ml-2' icon={faArrowRight} />
          </ActionLink>
        </div>
      </div>
      <div className='pb-4'>
        <Codeblock hasCopyButton={false} preClassName='break-all whitespace-pre-wrap' theme='dark' type='multi-line'>
          {success ? JSON.stringify(success) : 'See your response here'}
        </Codeblock>
      </div>
      <div className='xs:flex-col flex lg:flex-row lg:justify-between'>
        <div className='xs:order-2 xs:flex xs:flex-col lg:order-1 lg:flex-row'>
          <Button
            className='xs:order-2 h-max w-full lg:order-1 lg:w-auto '
            data-testid='getting-started-next'
            onClick={handlePrevStep}
            theme='secondary'
          >
            Previous
          </Button>
          <Button
            className='xs:order-1 xs:mb-4 h-max w-full lg:order-2 lg:mb-0 lg:ml-4 lg:w-auto'
            data-testid='getting-started-next'
            onClick={handleNextStep}
          >
            Next
          </Button>
        </div>
        <div className='xs:order-1 xs:mb-3 content-center lg:order-2 lg:mb-0'>
          <span>
            {success ? (
              <>
                <span className='text-success-default !text-sm'>
                  <FontAwesomeIcon className='fa-fw fa-check-circle' icon={faCheckCircle} />
                  <span> You’ve sent your first request! </span>
                </span>
                <Link href='/dashboard/stats/ethereum/15m/all-projects' legacyBehavior passHref>
                  <a className='text-sm' target='_blank'>
                    View the results in Stats
                  </a>
                </Link>
              </>
            ) : null}
          </span>
          {isError ? (
            <span className='!text-sm text-red-500'>
              <FontAwesomeIcon className='fa-fw fa-triangle-exclamation' icon={faTriangleExclamation} />
              <span> Request failed, please try again</span>
            </span>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default StepSendFirstRequest
